import { makeAutoObservable } from 'mobx';
import apiClient from '../utils/apiClient';
import logger from '../utils/logger';

class ProductPhotoStore {
  constructor() {
    makeAutoObservable(this);
  }

  // Состояние
  photos = [];
  photoTags = [];
  groupedTags = {};
  selectedTagIds = [];
  isLoading = false;
  error = null;

  // Getters
  get hasPhotos() {
    return this.photos.length > 0;
  }

  get filteredPhotos() {
    if (this.selectedTagIds.length === 0) {
      return this.photos;
    }

    return this.photos.filter(photo => {
      const photoTags = photo.tags || [];
      
      // Группируем выбранные теги по tag_group
      const selectedTagsByGroup = {};
      this.selectedTagIds.forEach(selectedTagId => {
        const tag = this.photoTags.find(t => t.id === selectedTagId);
        if (tag) {
          const group = tag.tag_group || 'Без группы';
          if (!selectedTagsByGroup[group]) {
            selectedTagsByGroup[group] = [];
          }
          selectedTagsByGroup[group].push(selectedTagId);
        }
      });
      
      // Проверяем каждую группу тегов
      // Внутри группы - ИЛИ (some), между группами - И (every)
      return Object.keys(selectedTagsByGroup).every(group => {
        const tagsInGroup = selectedTagsByGroup[group];
        const photoTagIds = photoTags.map(t => t.id);
        
        // Хотя бы один тег из этой группы должен быть у фото
        return tagsInGroup.some(tagId => photoTagIds.includes(tagId));
      });
    });
  }

  get hasActiveFilters() {
    return this.selectedTagIds.length > 0;
  }

  // Actions
  setLoading(loading) {
    this.isLoading = loading;
  }

  setError(error) {
    this.error = error;
  }

  clearError() {
    this.error = null;
  }

  setPhotos(photos) {
    this.photos = photos;
  }

  setTags(tags) {
    this.photoTags = tags;
  }

  setGroupedTags(groupedTags) {
    this.groupedTags = groupedTags;
  }

  setSelectedTagIds(tagIds) {
    this.selectedTagIds = tagIds;
  }

  toggleTag(tagId) {
    if (this.selectedTagIds.includes(tagId)) {
      this.selectedTagIds = this.selectedTagIds.filter(id => id !== tagId);
    } else {
      this.selectedTagIds = [...this.selectedTagIds, tagId];
    }
  }

  clearFilters() {
    this.selectedTagIds = [];
  }

  // Загрузка фотографий продукта
  async fetchProductPhotos(productId) {
    if (!productId) {
      logger.log('ProductPhotoStore.fetchProductPhotos: productId не указан');
      return;
    }

    this.setLoading(true);
    this.clearError();

    try {
      logger.log('📸 Загружаю фотографии продукта:', productId);

      const response = await apiClient.get(`/partnerships/partner-product-photos/${productId}`);
      
      if (response.data) {
        const photos = response.data.photos || [];
        const tags = response.data.tags || [];
        const groupedTags = response.data.groupedTags || {};

        this.setPhotos(photos);
        this.setTags(tags);
        this.setGroupedTags(groupedTags);
        
        // Сбрасываем фильтры при загрузке новых фотографий
        this.clearFilters();

        logger.log(`✅ Загружено ${photos.length} фотографий, ${tags.length} тегов`);
      }
    } catch (error) {
      logger.error('❌ Ошибка при загрузке фотографий продукта:', error);
      this.setError('Не удалось загрузить фотографии продукта');
    } finally {
      this.setLoading(false);
    }
  }

  // Очистка данных
  clear() {
    this.photos = [];
    this.photoTags = [];
    this.groupedTags = {};
    this.selectedTagIds = [];
    this.isLoading = false;
    this.error = null;
  }

  // Получение тегов для конкретной фотографии
  getPhotoTags(photoId) {
    const photo = this.photos.find(p => p.id === photoId);
    return photo?.tags || [];
  }

  // Проверка, есть ли у фотографии конкретный тег
  photoHasTag(photoId, tagId) {
    const photoTags = this.getPhotoTags(photoId);
    return photoTags.some(tag => tag.id === tagId);
  }
}

const productPhotoStore = new ProductPhotoStore();
export default productPhotoStore; 